import { Button, Dialog, Icon, sonner } from '@sr/ui-library';
import { useCallback, useState } from 'react';
import { useDeleteTitle } from '../api/titles/titlesApi';

const { toast } = sonner;

interface DraftDeleteActionProps {
  /**
   * Extra cache cleanup that runs once the delete has succeeded, before the
   * dialog closes. If it throws, the user sees the same error as a failed delete.
   */
  onDeleteSuccess?: () => Promise<void> | void;
  /** Runs after the dialog has closed, e.g. to leave a page about the deleted title. */
  onDeleted?: () => void;
  titleId: string;
}

/**
 * Delete button and dialog for a draft title. Confirming deletes the title; the
 * backend also deletes the draft's task, so callers drop it from their cached
 * task lists through `onDeleteSuccess`. A successful delete always ends with the
 * "Draft deleted" toast, shown by this component so every page gets it, after
 * the page's own `onDeleted`.
 */
export const DraftDeleteAction = ({
  onDeleteSuccess,
  onDeleted,
  titleId,
}: DraftDeleteActionProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const deleteTitle = useDeleteTitle();

  const handleConfirmDelete = useCallback(async () => {
    if (!titleId) return;

    setIsDeleting(true);
    try {
      await deleteTitle.mutateAsync(titleId);
      await onDeleteSuccess?.();

      setIsDialogOpen(false);
      onDeleted?.();
      toast.success('Draft deleted');
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to delete draft';

      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  }, [titleId, deleteTitle, onDeleteSuccess, onDeleted]);

  return (
    <>
      <Button
        aria-label="Delete this draft"
        disabled={isDeleting}
        sectionLeft={<Icon icon="delete" />}
        size="smIcon"
        variant="outline"
        onClick={() => setIsDialogOpen(true)}
      />

      <Dialog
        heading="Delete this draft?"
        actionButtonProps={{
          children: 'Delete',
          disabled: isDeleting,
          loading: isDeleting,
          onClick: handleConfirmDelete,
        }}
        cancelButtonProps={{ disabled: isDeleting }}
        open={isDialogOpen}
        onOpenChange={(open) => {
          if (!isDeleting) setIsDialogOpen(open);
        }}
      >
        <div className="font-sans text-base leading-normal">
          This action cannot be undone.
        </div>
      </Dialog>
    </>
  );
};
